import {
  Css,
  GridColumn,
  GridDataRow,
  RowStyles,
  Tag,
  Tokens,
  cardBadgeSlot,
  cardDataBlockSlot,
  cardEyebrowSlot,
  cardProgressSlot,
  cardStatusSlot,
  cardTitleSlot,
  column,
  simpleHeader,
} from "@homebound/beam";
import { ProgressPill } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { GlobalProductOfferingsDetailFragment } from "src/routes/libraries/product-offerings/GlobalProductOfferings.generated";
import { getDevelopmentOrProjectProductOfferingUrl } from "src/RouteUrls";
import { readyPlanStatusToTagType } from "src/utils";
import { programDataBathRange, programDataRange } from "src/utils/programData";
import { PLAN_FALLBACK_IMG } from "../plan-package/takeoffs/utils";

type HeaderRow = { kind: "header"; data: undefined };
type ProductOfferingRow = { kind: "productOffering"; data: GlobalProductOfferingsDetailFragment; id: string };
export type GlobalProductOfferingsTableRow = HeaderRow | ProductOfferingRow;

type CreateGlobalProductOfferingsColumnsOpts = { hideCommunity?: boolean };

export function createGlobalProductOfferingsColumns(
  opts: CreateGlobalProductOfferingsColumnsOpts = {},
): GridColumn<GlobalProductOfferingsTableRow>[] {
  const { hideCommunity } = opts;
  return [
    column<GlobalProductOfferingsTableRow>({
      id: "name",
      name: "Offering Name",
      header: "Offering Name",
      serverSideSortKey: "name",
      mw: "240px",
      productOffering: (po) => ({
        content: () => <div css={Css.xs.color(Tokens.TextLinkDefault).$}>{po.name}</div>,
        value: po.name,
        cardSlot: cardTitleSlot(po.name),
      }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "version",
      name: "Version",
      header: "Version",
      mw: "120px",
      productOffering: (po) => ({
        content: () => (
          <div css={Css.df.aic.gapPx(4).$}>
            {po.aggregateLatest.displayVersion}
            {!po.copyInProgress && !po.availableUpdates.isEmpty && po.status.code !== ReadyPlanStatus.Archived && (
              <Tag iconOnly icon="arrowFromBottom" type="update" text="Updates available" />
            )}
          </div>
        ),
        value: po.aggregateLatest.displayVersion,
        cardSlot: cardBadgeSlot(
          po.aggregateLatest.displayVersion,
          !po.copyInProgress && !po.availableUpdates.isEmpty && po.status.code !== ReadyPlanStatus.Archived
            ? [{ iconOnly: true, type: "update", text: "Updates available", icon: "arrowFromBottom" }]
            : [],
        ),
      }),
    }),
    ...(!hideCommunity
      ? [
          column<GlobalProductOfferingsTableRow>({
            id: "community",
            name: "Community",
            header: "Community",
            mw: "160px",
            productOffering: (po) => (po.project?.__typename === "Community" ? po.project.name : ""),
          }),
        ]
      : []),
    column<GlobalProductOfferingsTableRow>({
      id: "planCode",
      name: "Plan Code",
      header: "Plan Code",
      mw: "120px",
      productOffering: (po) => ({ content: po.planPackage.code, cardSlot: cardEyebrowSlot(po.planPackage.code ?? "") }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "sqft",
      name: "Sqft",
      header: "Sqft",
      mw: "120px",
      productOffering: ({ minProgramData, maxProgramData }) => ({
        content: programDataRange(minProgramData, maxProgramData, "sellableSqft"),
        cardSlot: cardDataBlockSlot({
          label: "Sqft",
          value: programDataRange(minProgramData, maxProgramData, "sellableSqft"),
        }),
      }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "beds",
      name: "Beds",
      header: "Beds",
      mw: "80px",
      productOffering: ({ minProgramData, maxProgramData }) => ({
        content: programDataRange(minProgramData, maxProgramData, "bedrooms"),
        cardSlot: cardDataBlockSlot({
          label: "Beds",
          value: programDataRange(minProgramData, maxProgramData, "bedrooms"),
        }),
      }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "baths",
      name: "Baths",
      header: "Baths",
      mw: "80px",
      productOffering: ({ minProgramData, maxProgramData }) => ({
        content: programDataBathRange(minProgramData, maxProgramData),
        cardSlot: cardDataBlockSlot({ label: "Baths", value: programDataBathRange(minProgramData, maxProgramData) }),
      }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "elevations",
      name: "Elevations",
      header: "Elevations",
      mw: "100px",
      productOffering: (po) => ({
        content: po.activeElevationCount,
        cardSlot: cardDataBlockSlot({ label: "Elevations", value: po.activeElevationCount }),
      }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "width",
      name: "Width",
      header: "Width",
      mw: "90px",
      productOffering: ({ minProgramData, maxProgramData }) => ({
        content: programDataRange(minProgramData, maxProgramData, "widthInFeet"),
        cardSlot: cardDataBlockSlot({
          label: "Width",
          value: programDataRange(minProgramData, maxProgramData, "widthInFeet"),
        }),
      }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "depth",
      name: "Depth",
      header: "Depth",
      mw: "90px",
      productOffering: ({ minProgramData, maxProgramData }) => ({
        content: programDataRange(minProgramData, maxProgramData, "depthInFeet"),
        cardSlot: cardDataBlockSlot({
          label: "Depth",
          value: programDataRange(minProgramData, maxProgramData, "depthInFeet"),
        }),
      }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "status",
      name: "Status",
      header: "Status",
      mw: "100px",
      productOffering: (po) => ({
        content: () => (
          <div css={Css.df.aic.gap1.$}>
            <StatusIndicator status={po.status.code} type="ReadyPlan" />
            {po.status.name}
          </div>
        ),
        value: po.status.name,
        cardSlot: cardStatusSlot({ text: po.status.name, type: readyPlanStatusToTagType[po.status.code] }),
      }),
    }),
    column<GlobalProductOfferingsTableRow>({
      id: "bidOut",
      name: "Bid Out",
      header: "Bid Out",
      mw: "140px",
      productOffering: (po) => ({
        content: () => <ProgressPill progress={po.bidOutPercentage} changeColorOnCompleted />,
        value: po.bidOutPercentage,
        cardSlot: cardProgressSlot(po.bidOutPercentage),
      }),
    }),
  ];
}

export function createGlobalProductOfferingsRows(
  productOfferings: GlobalProductOfferingsDetailFragment[],
): GridDataRow<GlobalProductOfferingsTableRow>[] {
  return [
    simpleHeader,
    ...productOfferings.map((po) => ({
      kind: "productOffering" as const,
      data: po,
      id: po.id,
      imgSrc: po.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG,
    })),
  ];
}

export function createGlobalProductOfferingsRowStyles(): RowStyles<GlobalProductOfferingsTableRow> {
  return { productOffering: { rowLink: ({ data }) => getDevelopmentOrProjectProductOfferingUrl(data) } };
}
